// src/odoo/customers/hooks/useProductInsights.ts
// All comments are in English.

import { useQuery } from "@tanstack/react-query";

import {
  fetchProductInsights,
  fetchCustomerProductSummary,
  fetchCustomerProductPurchases,
  fetchCustomerProductRecommendations,
  type ProductInsightsResponse,
  type CustomerProductSummary,
  type CustomerProductPurchasesResponse,
  type CustomerProductRecommendationsResponse,
} from "../api/productInsightsApi";
import { type DateRangeQuery } from "../api/overviewApi";

// Root query key for all customer-related queries.
export const CUSTOMERS_QUERY_KEY = "customers";

/**
 * Company-level product insights (top products / categories).
 * This is typically used in overview or side analytics,
 * not in the dedicated Product Insights tab.
 */
export function useCompanyProductInsights(
  companyPartnerId?: number,
  dateRange?: DateRangeQuery
) {
  return useQuery<ProductInsightsResponse>({
    queryKey: [
      CUSTOMERS_QUERY_KEY,
      "company-product-insights",
      companyPartnerId,
      dateRange ?? null,
    ],
    queryFn: () =>
      fetchProductInsights(companyPartnerId as number, dateRange),
    enabled: !!companyPartnerId,
  });
}

/**
 * High-level product summary for the Product Insights tab.
 * Now supports an optional dateRange filter.
 */
export function useCustomerProductSummary(
  companyPartnerId?: number,
  dateRange?: DateRangeQuery
) {
  return useQuery<CustomerProductSummary>({
    queryKey: [
      CUSTOMERS_QUERY_KEY,
      "product-summary",
      companyPartnerId,
      dateRange ?? null,
    ],
    queryFn: () =>
      fetchCustomerProductSummary(companyPartnerId as number, dateRange),
    enabled: !!companyPartnerId,
  });
}

/**
 * Product purchases distribution for the Product Insights tab.
 */
export function useCustomerProductPurchases(
  companyPartnerId?: number,
  dateRange?: DateRangeQuery
) {
  return useQuery<CustomerProductPurchasesResponse>({
    queryKey: [
      CUSTOMERS_QUERY_KEY,
      "product-purchases",
      companyPartnerId,
      dateRange ?? null,
    ],
    queryFn: () =>
      fetchCustomerProductPurchases(companyPartnerId as number, dateRange),
    enabled: !!companyPartnerId,
  });
}

/**
 * Product recommendations (cross-sell based) for the Product Insights tab.
 *
 * Notes:
 * - minLift, minConfidence, minSupport are optional and passed through
 *   to the backend for additional filtering.
 * - companyId is the internal company_id (optional). Recommendations
 *   can still work with only companyPartnerId.
 */
export function useCustomerProductRecommendations(params: {
  companyPartnerId?: number;
  companyId?: number | null;
  limit?: number;
  minLift?: number | null;
  minConfidence?: number | null;
  minSupport?: number | null;
}) {
  const {
    companyPartnerId,
    companyId,
    limit = 10,
    minLift = null,
    minConfidence = null,
    minSupport = null,
  } = params;

  return useQuery<CustomerProductRecommendationsResponse>({
    queryKey: [
      CUSTOMERS_QUERY_KEY,
      "product-recommendations",
      companyPartnerId,
      companyId ?? null,
      limit,
      minLift,
      minConfidence,
      minSupport,
    ],
    queryFn: () =>
      fetchCustomerProductRecommendations(companyPartnerId as number, {
        companyId,
        limit,
        minLift,
        minConfidence,
        minSupport,
      }),
    // Allow recommendations even when companyId is not provided
    enabled: !!companyPartnerId,
  });
}
